/**
 * @fileoverview Owner → QR Reward Templates — the pre-approved offers attendants
 * can generate reward QR codes from. Attendants can ONLY issue from these
 * templates, so the template's offer/uses/expiry IS the control (no daily caps).
 * Generated QRs appear in Programs → vouchers with a "by <attendant>" tag and
 * can be closed/revoked there.
 */
import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Switch } from 'react-native';
import { Stack, useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Input from '../../../src/components/Input';
import Picker from '../../../src/components/Picker';
import ContentContainer from '../../../src/components/ContentContainer';
import InlineConfirm from '../../../src/components/InlineConfirm';
import { useToast } from '../../../src/components/Toast';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';

interface RewardTemplate {
  id: string;
  name: string;
  kind: 'promo' | 'gift';
  discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit';
  percentOff: number | null;
  tierName: string | null;
  freeMinutes: number | null;
  freeVendCents?: number | null;
  creditCents?: number | null;
  bayType: string | null;
  appliesTo?: { tierIds: string[]; selfServe: boolean; vacuum?: boolean; vending?: boolean } | null;
  appliesToLabel?: string | null;
  locationId: string | null;
  redemptionLimit: number;
  perCustomerLimit: number;
  expiresInDays: number;
  isActive: boolean;
}
interface Location { id: string; name: string }

function offerLabel(t: RewardTemplate): string {
  if (t.discountKind === 'free_vend') {
    return t.freeVendCents != null ? `$${(t.freeVendCents / 100).toFixed(2)} vending credit` : 'Vending credit';
  }
  if (t.discountKind === 'credit') {
    return t.creditCents != null ? `$${(t.creditCents / 100).toFixed(2)} gift credit` : 'Gift credit';
  }
  if (t.discountKind === 'free_minutes') {
    return t.appliesTo?.vacuum ? `${t.freeMinutes ?? '?'} free minutes (self-serve + vacuums)` : `${t.freeMinutes ?? '?'} free self-serve minutes`;
  }
  // Multi-select rows: "Free wash · Ultimate, Deluxe + Self-serve" style.
  if (t.discountKind === 'free_wash' || t.percentOff === 100) {
    if (t.appliesToLabel) return `Free wash · ${t.appliesToLabel}`;
    return t.tierName ? `Free ${t.tierName} wash` : 'Free wash';
  }
  if (t.appliesToLabel) return `${t.percentOff}% off · ${t.appliesToLabel}`;
  return `${t.percentOff}% off${t.tierName ? ` ${t.tierName}` : ''}`;
}

export default function RewardTemplatesScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();

  const [templates, setTemplates] = useState<RewardTemplate[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Form
  const [formVisible, setFormVisible] = useState(false);
  const [editing, setEditing] = useState<RewardTemplate | null>(null);
  const [name, setName] = useState('');
  const [offerType, setOfferType] = useState<'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit'>('percent');
  const [percentOff, setPercentOff] = useState('25');
  const [freeMinutes, setFreeMinutes] = useState('10');
  // free_minutes scope: self-serve is REQUIRED by the backend for templates;
  // vacuums is an optional add-on.
  const [minutesVacuum, setMinutesVacuum] = useState(false);
  // Free vending credit in dollars ($0.25–$100 → freeVendCents); vending-only scope forced.
  const [freeVendAmount, setFreeVendAmount] = useState('2');
  // Gift credit in dollars ($1–$500 → creditCents); scope-free.
  const [creditAmount, setCreditAmount] = useState('10');
  // Multi-select applicability for percent/free_wash templates. Defaults (all
  // tiers checked, rest unchecked) preserve the old automatic-only behavior.
  // Vacuums/Vending checkboxes are percent-only (free_wash can't apply there),
  // mirroring the main voucher forms.
  const [selectedTierIds, setSelectedTierIds] = useState<string[]>([]);
  const [selfServe, setSelfServe] = useState(false);
  const [vacuum, setVacuum] = useState(false);
  const [vending, setVending] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [washTiers, setWashTiers] = useState<{ id: string; name: string; priceInCents: number; displayOrder: number; isActive: boolean }[]>([]);
  const [locationId, setLocationId] = useState<string>('all');
  const [expiresInDays, setExpiresInDays] = useState('30');
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    try {
      const [t, l, tiers] = await Promise.all([
        api<{ templates: RewardTemplate[] }>('/api/owner/reward-templates'),
        api<Location[]>('/api/locations').catch(() => [] as Location[]),
        api<{ id: string; name: string; priceInCents: number; displayOrder: number; isActive: boolean }[]>('/api/wash-tiers').catch(() => []),
      ]);
      setTemplates(t.templates);
      setLocations(Array.isArray(l) ? l : []);
      setWashTiers(Array.isArray(tiers) ? tiers.filter(x => x.isActive).sort((a, b) => a.displayOrder - b.displayOrder) : []);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };
  useFocusEffect(useCallback(() => { fetchAll(); }, []));

  const openCreate = () => {
    setEditing(null);
    setName(''); setOfferType('percent'); setPercentOff('25'); setFreeMinutes('10');
    setMinutesVacuum(false); setFreeVendAmount('2'); setCreditAmount('10');
    setSelectedTierIds(washTiers.map(x => x.id)); setSelfServe(false); setVacuum(false); setVending(false); setScopeError(null);
    setLocationId('all'); setExpiresInDays('30');
    setFormVisible(true);
  };

  const openEdit = (t: RewardTemplate) => {
    setEditing(t);
    setName(t.name);
    setOfferType(t.discountKind);
    setPercentOff(String(t.percentOff ?? 25));
    setFreeMinutes(String(t.freeMinutes ?? 10));
    setMinutesVacuum(!!t.appliesTo?.vacuum);
    setFreeVendAmount(String(((t.freeVendCents ?? 200) / 100)));
    setCreditAmount(String(((t.creditCents ?? 1000) / 100)));
    // Seed the checkboxes: appliesTo rows verbatim; legacy rows map from the
    // old single-value scope (tierName → that tier; bayType → tier/self-serve
    // coverage). Saving upgrades a legacy row to appliesTo.
    if (t.appliesTo) {
      setSelectedTierIds(t.appliesTo.tierIds.filter(id => washTiers.some(x => x.id === id)));
      setSelfServe(t.appliesTo.selfServe);
      setVacuum(!!t.appliesTo.vacuum);
      setVending(!!t.appliesTo.vending);
    } else if (t.tierName) {
      const match = washTiers.find(x => x.name.toLowerCase() === t.tierName!.toLowerCase());
      setSelectedTierIds(match ? [match.id] : []);
      setSelfServe(false); setVacuum(false); setVending(false);
    } else if (t.bayType === 'self-serve') {
      setSelectedTierIds([]);
      setSelfServe(true); setVacuum(false); setVending(false);
    } else {
      setSelectedTierIds(washTiers.map(x => x.id));
      setSelfServe(t.bayType == null); setVacuum(false); setVending(false);
    }
    setScopeError(null);
    setLocationId(t.locationId ?? 'all');
    setExpiresInDays(String(t.expiresInDays));
    setFormVisible(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Template name is required' });
      return;
    }
    const body: any = {
      name: name.trim(),
      kind: offerType === 'percent' ? 'promo' : 'gift',
      discountKind: offerType,
      locationId: locationId === 'all' ? null : locationId,
      expiresInDays: Math.max(1, parseInt(expiresInDays) || 30),
    };
    if (offerType === 'percent') {
      const pct = parseInt(percentOff);
      if (!pct || pct < 1 || pct > 99) {
        toast.show({ type: 'error', title: 'Error', message: 'Percent must be 1–99' });
        return;
      }
      body.percentOff = pct;
    }
    if (offerType === 'free_minutes') {
      const mins = parseInt(freeMinutes);
      if (!mins || mins < 1 || mins > 240) {
        toast.show({ type: 'error', title: 'Error', message: 'Minutes must be 1–240' });
        return;
      }
      body.freeMinutes = mins;
      body.tierName = null;
      // Backend rule: template free_minutes MUST include self-serve (no tiers);
      // vacuums is an optional add-on carried via appliesTo.
      if (minutesVacuum) {
        body.appliesTo = { tierIds: [], selfServe: true, vacuum: true };
      } else {
        body.appliesTo = null;
        body.bayType = 'self-serve';
      }
    } else if (offerType === 'free_vend') {
      const cents = Math.round((parseFloat(freeVendAmount) || 0) * 100);
      if (cents < 25 || cents > 10000) {
        toast.show({ type: 'error', title: 'Error', message: 'Vending credit must be $0.25–$100' });
        return;
      }
      body.freeVendCents = cents;
      body.tierName = null;
      // Backend rule: free_vend templates must apply to vending ONLY.
      body.appliesTo = { tierIds: [], selfServe: false, vending: true };
    } else if (offerType === 'credit') {
      const cents = Math.round((parseFloat(creditAmount) || 0) * 100);
      if (cents < 100 || cents > 50000) {
        toast.show({ type: 'error', title: 'Error', message: 'Credit must be $1–$500' });
        return;
      }
      body.creditCents = cents;
      body.tierName = null;
      // Scope-free: balance dollars deposited at claim, spendable anywhere.
      body.appliesTo = null;
      body.bayType = null;
    } else {
      // Percent / free wash carry the multi-select applicability. The backend
      // derives the legacy tierName/bayType shim from it. Vacuums/Vending are
      // percent-only (free_wash can't apply there — backend rejects).
      if (selectedTierIds.length === 0 && !selfServe && !(offerType === 'percent' && (vacuum || vending))) {
        setScopeError('Select at least one wash type the offer applies to.');
        return;
      }
      if (offerType === 'free_wash' && selectedTierIds.length === 0) {
        setScopeError('A free wash needs at least one wash tier selected.');
        return;
      }
      body.appliesTo = {
        tierIds: selectedTierIds,
        selfServe,
        vacuum: offerType === 'percent' ? vacuum : false,
        vending: offerType === 'percent' ? vending : false,
      };
      body.tierName = null;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await api(`/api/owner/reward-templates/${editing.id}`, { method: 'PATCH', body });
        toast.show({ type: 'success', title: 'Template updated' });
      } else {
        await api('/api/owner/reward-templates', { method: 'POST', body });
        toast.show({ type: 'success', title: 'Template created' });
      }
      setFormVisible(false);
      fetchAll();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (t: RewardTemplate) => {
    try {
      await api(`/api/owner/reward-templates/${t.id}`, { method: 'PATCH', body: { isActive: !t.isActive } });
      fetchAll();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const doDelete = async (id: string) => {
    try {
      await api(`/api/owner/reward-templates/${id}`, { method: 'DELETE' });
      setConfirmDeleteId(null);
      toast.show({ type: 'success', title: 'Template removed' });
      fetchAll();
    } catch (err: any) {
      setConfirmDeleteId(null);
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  return (
    <>
      <Stack.Screen options={{
        title: 'QR Reward Templates',
        headerLeft: () => (
          <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/owner/staff')} hitSlop={12} style={{ padding: spacing.sm, marginLeft: -spacing.xs }}>
            <Ionicons name="arrow-back" size={28} color={colors.primary} />
          </TouchableOpacity>
        ),
      }} />
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={720}>
          <Text style={styles.title}>QR Reward Templates</Text>
          <Text style={styles.subtitle}>
            Attendants with the "Generate Reward QRs" permission can create QR codes from these
            templates only — the offer, locations, uses, and expiry you set here are the limits.
            Every generated QR shows up under Programs with the attendant's name, where you can close it.
          </Text>

          {!formVisible && (
            <Button
              title="New Template"
              onPress={openCreate}
              icon={<Ionicons name="add-circle" size={18} color={colors.white} />}
              size="sm"
              style={{ marginBottom: spacing.lg, alignSelf: 'flex-start' }}
            />
          )}

          {formVisible && (
            <Card style={{ marginBottom: spacing.lg }}>
              <Text style={styles.sectionTitle}>{editing ? 'Edit Template' : 'New Template'}</Text>
              <Input label="Name (what attendants see)" placeholder="e.g. Service recovery — free basic wash" value={name} onChangeText={setName} />

              <Text style={styles.fieldLabel}>Offer</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
                {([['percent', '% off'], ['free_wash', 'Free wash'], ['free_minutes', 'Free minutes'], ['free_vend', 'Free vending credit ($)'], ['credit', 'Credit $']] as const).map(([val, label]) => (
                  <TouchableOpacity key={val} onPress={() => { setScopeError(null); setOfferType(val); }} style={[styles.chip, offerType === val && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                    <Text style={[styles.chipText, offerType === val && { color: '#fff' }]}>{label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              {offerType === 'percent' && <Input label="Percent off (1–99)" value={percentOff} onChangeText={setPercentOff} keyboardType="number-pad" />}
              {(offerType === 'percent' || offerType === 'free_wash') && (
                <>
                  <Text style={styles.fieldLabel}>Applies to</Text>
                  {washTiers.map(t => (
                    <TouchableOpacity
                      key={t.id}
                      style={styles.checkRow}
                      onPress={() => {
                        setScopeError(null);
                        setSelectedTierIds(prev => prev.includes(t.id) ? prev.filter(x => x !== t.id) : [...prev, t.id]);
                      }}
                    >
                      <View style={[styles.checkbox, selectedTierIds.includes(t.id) && styles.checkboxActive]}>
                        {selectedTierIds.includes(t.id) && <Text style={styles.checkmark}>✓</Text>}
                      </View>
                      <Text style={styles.checkLabel}>{t.name} (${(t.priceInCents / 100).toFixed(2)})</Text>
                    </TouchableOpacity>
                  ))}
                  <TouchableOpacity style={styles.checkRow} onPress={() => { setScopeError(null); setSelfServe(v => !v); }}>
                    <View style={[styles.checkbox, selfServe && styles.checkboxActive]}>
                      {selfServe && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkLabel}>Self-serve bays</Text>
                  </TouchableOpacity>
                  {/* Vacuums + Vending: percent-only — free_wash can't apply there
                      (backend rejects), so the boxes disable with reduced opacity. */}
                  <TouchableOpacity
                    style={[styles.checkRow, offerType !== 'percent' && { opacity: 0.4 }]}
                    onPress={() => { setScopeError(null); setVacuum(v => !v); }}
                    disabled={offerType !== 'percent'}
                  >
                    <View style={[styles.checkbox, offerType === 'percent' && vacuum && styles.checkboxActive]}>
                      {offerType === 'percent' && vacuum && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkLabel}>Vacuums</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.checkRow, offerType !== 'percent' && { opacity: 0.4 }]}
                    onPress={() => { setScopeError(null); setVending(v => !v); }}
                    disabled={offerType !== 'percent'}
                  >
                    <View style={[styles.checkbox, offerType === 'percent' && vending && styles.checkboxActive]}>
                      {offerType === 'percent' && vending && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkLabel}>Vending machines</Text>
                  </TouchableOpacity>
                  {scopeError && <Text style={styles.errorText}>{scopeError}</Text>}
                </>
              )}
              {offerType === 'free_minutes' && (
                <>
                  <Input label="Free minutes (1–240)" value={freeMinutes} onChangeText={setFreeMinutes} keyboardType="number-pad" />
                  <Text style={styles.fieldLabel}>Applies to</Text>
                  <View style={[styles.checkRow, { opacity: 0.6 }]}>
                    <View style={[styles.checkbox, styles.checkboxActive]}>
                      <Text style={styles.checkmark}>✓</Text>
                    </View>
                    <Text style={styles.checkLabel}>Self-serve bays (always included)</Text>
                  </View>
                  <TouchableOpacity style={styles.checkRow} onPress={() => setMinutesVacuum(v => !v)}>
                    <View style={[styles.checkbox, minutesVacuum && styles.checkboxActive]}>
                      {minutesVacuum && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkLabel}>Vacuums</Text>
                  </TouchableOpacity>
                </>
              )}
              {offerType === 'free_vend' && (
                <>
                  <Input label="Vending credit ($0.25–$100)" value={freeVendAmount} onChangeText={setFreeVendAmount} keyboardType="decimal-pad" />
                  <Text style={styles.meta}>Vending machines only — free vending credit can't apply to washes or vacuums.</Text>
                </>
              )}
              {offerType === 'credit' && (
                <>
                  <Input label="Gift credit ($1–$500)" value={creditAmount} onChangeText={setCreditAmount} keyboardType="decimal-pad" />
                  <Text style={styles.meta}>Balance dollars deposited when the customer claims the QR — spendable anywhere at your locations.</Text>
                </>
              )}

              <Text style={styles.fieldLabel}>Valid at</Text>
              <Picker
                options={[{ label: 'All locations', value: 'all' }, ...locations.map(l => ({ label: l.name, value: l.id }))]}
                value={locationId}
                onValueChange={(v: string) => setLocationId(v)}
              />

              <Input label="Expires in (days)" value={expiresInDays} onChangeText={setExpiresInDays} keyboardType="number-pad" />
              <Text style={styles.meta}>Every generated QR is single-use — one QR per customer, tracked per attendant.</Text>

              <View style={{ flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm }}>
                <Button title="Cancel" variant="outline" size="sm" onPress={() => setFormVisible(false)} />
                <Button title={editing ? 'Save' : 'Create Template'} size="sm" onPress={handleSave} loading={submitting} />
              </View>
            </Card>
          )}

          {loading ? (
            <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
          ) : templates.length === 0 ? (
            <Card><Text style={styles.empty}>No templates yet. Create one so attendants can hand out rewards.</Text></Card>
          ) : (
            templates.map(t => (
              <Card key={t.id} style={{ marginBottom: spacing.md }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.name}>{t.name}</Text>
                    <Text style={styles.meta}>
                      {offerLabel(t)} · {t.locationId ? (locations.find(l => l.id === t.locationId)?.name ?? '1 location') : 'all locations'} · single use · {t.expiresInDays}d expiry
                    </Text>
                    {!t.isActive && <Text style={[styles.meta, { color: colors.danger }]}>Inactive — hidden from attendants</Text>}
                  </View>
                  <Switch
                    value={t.isActive}
                    onValueChange={() => toggleActive(t)}
                    trackColor={{ false: colors.borderLight, true: colors.success + '80' }}
                    thumbColor={t.isActive ? colors.success : colors.textLight}
                  />
                  <TouchableOpacity onPress={() => openEdit(t)} hitSlop={8} style={{ padding: spacing.xs }}>
                    <Ionicons name="pencil" size={20} color={colors.primary} />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setConfirmDeleteId(t.id)} hitSlop={8} style={{ padding: spacing.xs }}>
                    <Ionicons name="trash-outline" size={20} color={colors.danger} />
                  </TouchableOpacity>
                </View>
                {confirmDeleteId === t.id && (
                  <InlineConfirm
                    message={`Delete "${t.name}"? QRs already generated from it keep working.`}
                    visible={true}
                    onConfirm={() => doDelete(t.id)}
                    onCancel={() => setConfirmDeleteId(null)}
                    confirmLabel="Delete"
                    variant="danger"
                  />
                )}
              </Card>
            ))
          )}
          <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold as any, color: c.text, marginBottom: spacing.xs },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.lg, lineHeight: 20 },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text, marginBottom: spacing.sm },
  fieldLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium as any, color: c.textSecondary, marginBottom: spacing.xs },
  chip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  chipText: { fontSize: fontSize.sm, color: c.text },
  checkRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm },
  checkbox: { width: 20, height: 20, borderRadius: 4, borderWidth: 2, borderColor: c.border, justifyContent: 'center', alignItems: 'center' },
  checkboxActive: { backgroundColor: c.primary, borderColor: c.primary },
  checkmark: { color: c.white, fontSize: 12, fontWeight: fontWeight.bold as any },
  checkLabel: { fontSize: fontSize.sm, color: c.text, marginLeft: spacing.sm, flexShrink: 1 },
  errorText: { fontSize: fontSize.sm, color: c.danger, marginBottom: spacing.sm },
  name: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  empty: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', paddingVertical: spacing.lg },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
